import { useMemo, useState } from "react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { FEATURED } from "@/data/products";
import { useCatalog } from "@/hooks/useCatalog";
import { GALLERY } from "@/constants/testIds";
import { Radio } from "lucide-react";

const CATEGORIES = [
  "All",
  "Figures & Toys",
  "Home & Decor",
  "Cosplay & Gaming",
  "Art & Sculpture",
  "Jewelry",
  "Functional",
];

export const Gallery = ({ category, onCategoryChange }) => {
  const [internal, setInternal] = useState("All");
  const active = category ?? internal;
  const setActive = onCategoryChange ?? setInternal;
  const { products, connected, loading } = useCatalog();

  // "Featured" view: prefer curated slugs when present, otherwise fall back to
  // tagged items and then natural order so live-feed catalogs work too.
  const featuredProducts = useMemo(() => {
    const bySlug = new Map(products.map((p) => [p.slug, p]));
    const curated = FEATURED.map((s) => bySlug.get(s)).filter(Boolean);
    if (curated.length >= 8) return curated;
    const tagged = products.filter((p) => p.tag);
    const merged = [...curated, ...tagged, ...products];
    const seen = new Set();
    return merged.filter((p) => (seen.has(p.slug) ? false : seen.add(p.slug)));
  }, [products]);

  const filtered = useMemo(() => {
    const base =
      active === "All"
        ? featuredProducts
        : products.filter((p) => p.category === active);
    return base.slice(0, 12);
  }, [active, featuredProducts, products]);

  return (
    <section
      id="marketplace"
      data-testid={GALLERY.section}
      className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
    >
      <Reveal>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-neo-cyan">
            Premium Quality
          </span>
          {connected && (
            <span
              data-testid="feed-live-badge"
              className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(0,229,255,0.4)] bg-[rgba(0,229,255,0.1)] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-neo-cyan"
            >
              <Radio className="h-3 w-3 animate-pulse" /> Live feed
            </span>
          )}
        </div>
        <h2 className="mt-3 max-w-2xl font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          A marketplace of museum-grade prints
        </h2>
        <p className="mt-3 max-w-xl text-base text-muted-foreground">
          Every print is made to order by a verified maker and inspected
          before it ships. Discover designs you won't find anywhere else.
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-8 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
                active === c
                  ? "border-[rgba(0,229,255,0.5)] bg-[rgba(0,229,255,0.14)] text-foreground shadow-[var(--glow-cyan)]"
                  : "border-border/70 bg-secondary/40 text-muted-foreground hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </Reveal>

      {filtered.length === 0 ? (
        <p className="mt-10 text-sm text-muted-foreground">
          {loading ? "Loading prints\u2026" : "No prints in this category yet."}
        </p>
      ) : (
        <StaggerGroup className="mt-8 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
          {filtered.map((p, i) => (
            <StaggerItem
              key={p.slug}
              className={i === 0 ? "col-span-2 row-span-2 lg:col-span-2 lg:row-span-2" : ""}
            >
              <div className="h-full">
                <ProductCard product={p} size={i === 0 ? "lg" : "md"} />
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      )}
    </section>
  );
};
